import {
  PaymentFlow,
  PaymentFlowFeatureFlag,
  PaymentFlowPolicies,
  PaymentFlowType,
} from "../../../apis/requests/payment-flows/types";
import {ConditionGroup, ParamSchema, SegmentCondition} from "../../../apis/requests/segments/types";
import {findParam, normalizeSubmitValue} from "../../segments/segment-form/helpers";
import {isDefaultFlow} from "../helpers";
import {CENTS_PARAMETERS, CENTS_PER_DOLLAR, CONTROL_VARIANT_NAME, EMPTY_CONDITION_GROUP} from "./constants";
import type {BaseFlowFormValues, ConditionFormValues} from "./schema";

export const findDefaultFlow = <T extends PaymentFlowType>(flows: PaymentFlow<T>[]): PaymentFlow<T> | undefined =>
  flows.find(isDefaultFlow);

export const getProductionPolicy = <T extends PaymentFlowType>(
  defaultFlow: PaymentFlow<T> | undefined,
  variantName: string
): PaymentFlowPolicies[T] | undefined => {
  const variants = defaultFlow?.config.variants;
  if (!variants) return undefined;
  return variants[variantName]?.policy ?? Object.values(variants)[0]?.policy;
};

export const getDefaultControlPolicy = <T extends PaymentFlowType>(
  defaultFlow: PaymentFlow<T> | undefined
): PaymentFlowPolicies[T] | undefined => defaultFlow?.config.variants[CONTROL_VARIANT_NAME]?.policy;

export const getNewFlowPriority = (flows: PaymentFlow[]): number =>
  flows.length === 0 ? 0 : Math.max(...flows.map((flow) => flow.priority)) + 1;

const toConditionValue = (
  param: ParamSchema | undefined,
  condition: ConditionFormValues
): SegmentCondition["value"] => {
  const value = normalizeSubmitValue(param, condition.operator, condition.value);
  if (typeof value === "number" && CENTS_PARAMETERS.has(condition.parameter)) {
    return Math.round(value * CENTS_PER_DOLLAR);
  }
  return value;
};

export const buildConditionsPayload = (values: BaseFlowFormValues, parameters: ParamSchema[]): ConditionGroup => {
  const groups = values.groups.filter((group) => group.conditions.length > 0);
  const isSingleGroup = groups.length === 1;
  return {
    match_mode: values.group_match_mode,
    rules: groups.map((group, index) => ({
      name: group.name.trim() || (isSingleGroup ? values.name.trim() : `Group ${index + 1}`),
      match_mode: group.match_mode,
      rules: group.conditions.map((condition) => ({
        parameter: condition.parameter,
        operator: condition.operator,
        value: toConditionValue(findParam(parameters, condition.parameter), condition),
      })),
    })),
  };
};

const fromConditionValue = (condition: SegmentCondition): unknown =>
  typeof condition.value === "number" && CENTS_PARAMETERS.has(condition.parameter)
    ? condition.value / CENTS_PER_DOLLAR
    : condition.value;

export const flowToBaseForm = (flow: PaymentFlow): BaseFlowFormValues => ({
  name: flow.name,
  feature_flag: flow.config.feature_flag,
  group_match_mode: flow.conditions.match_mode,
  groups:
    flow.conditions.rules.length > 0
      ? flow.conditions.rules.map((group) => ({
          name: group.name ?? "",
          match_mode: group.match_mode,
          conditions: group.rules.map((condition) => ({
            parameter: condition.parameter,
            operator: condition.operator,
            value: fromConditionValue(condition),
          })),
        }))
      : [EMPTY_CONDITION_GROUP],
});

export const withCurrentFlag = (
  featureFlags: PaymentFlowFeatureFlag[],
  flow: PaymentFlow | undefined
): PaymentFlowFeatureFlag[] => {
  if (!flow || featureFlags.some((flag) => flag.name === flow.config.feature_flag)) return featureFlags;
  const currentFlag: PaymentFlowFeatureFlag = {
    name: flow.config.feature_flag,
    variant_weights: Object.fromEntries(
      Object.entries(flow.config.variants).map(([name, variant]) => [name, variant.weight])
    ),
  };
  return [currentFlag, ...featureFlags];
};
